import { GetTrackingStatus } from "core/utils/EditionUtils";
import { GlamboxSessionStore } from "core/glambox/GlamboxSessionStore";

/**
 * core/utils/SubscriberUtils
 * ----------------------------------------------------------------------
 * Funções utilitárias para dados de subscribers da Glambox.
 *
 * @since     0.0.1
 */

/**
 * Gera um objeto com dados "essenciais" para exibição da edição no painel.
 *
 * @param {object} subscriberEdition
 *     Objeto contendo dados da edição para o subscriber
 * @returns {object}
 */
export const HandleSubscriberData = (subscriberEdition) => {
  let trackingStatus = GetTrackingStatus(subscriberEdition),
      boxId = `${subscriberEdition.boxId} (${trackingStatus.title}`;

  return {
    status: trackingStatus,
    boxId: boxId,
    subscriberId: subscriberEdition.subscriberId,
    editionId: subscriberEdition.editionId,
    edition: subscriberEdition
  };
};

/**
 * Filtra subscribers por edições e tipo de caixa, retornando-os em um objeto
 * cujas chaves são os tipos de caixas (`glambox`/`superbox`), além de uma
 * lista de `subscriberEditions` contendo subscribers por edição.
 *
 * @param {object[]} editions
 *     Lista com objetos referentes à edições do usuário
 * @param {number[]|string[]} possibleSubscriberIds
 *     Lista contendo `subscriberId` para filtragem de dados
 * @returns {object}
 */
export const FilterSubscriberByEditions = (
  editions,
  possibleSubscriberIds
) => {
  let editionsSliced = [ ...editions ],
      toRemove = 0,
      subscriberEditions = {},
      glambox,
      superbox;

  for (let n = 0; n < editions.length; n++) {
    let edition = editions[n],
        editionId = parseInt(edition.editionId),
        previousValue = subscriberEditions[editionId];

    if (possibleSubscriberIds.includes(edition.subscriberId)) {
      if (previousValue === undefined || previousValue === null) {
        subscriberEditions[editionId] = [];

        subscriberEditions[editionId].push(
          HandleSubscriberData(edition)
        );
      } else {
        subscriberEditions[editionId].push(
          HandleSubscriberData(edition)
        );

        editionsSliced.splice(n - toRemove, 1);
        toRemove++;
      }
    } else {
      editionsSliced.splice(n - toRemove, 1);
      toRemove++;
    }
  }

  editionsSliced.sort((a, b) => {
    return b.editionId - a.editionId;
  });

  glambox = editionsSliced.filter((item) => {
    return (parseInt(item.editionId / 1000000) !== 2);
  });

  superbox = editionsSliced.filter((item) => {
    return (parseInt(item.editionId / 1000000) === 2);
  });

  return {
    glambox,
    superbox,
    subscriberEditions
  };
};

/**
 * Verifica se algum dos subscribers de uma lista está ativo.
 *
 * @param {object[]} subscriberList
 *     Lista contendo subscribers para leitura
 * @returns {boolean}
 */
export const HasActiveSubscriber = (subscriberList) => {
  if (
    !subscriberList
      || !Array.isArray(subscriberList)
      || (Array.isArray(subscriberList) && subscriberList.length === 0)
  ) {
    return false;
  }

  for (let n = 0; n < subscriberList.length; n++) {
    let curr = subscriberList[n];

    if (curr.subscriberStatus === 0 && curr.subscriptionId === 1) {
      return true;
    }
  }

  return false;
};

/**
 * Separa a lista de subscribers fornecidos e os separa entre `glambox` e
 * `superbox`.
 *
 * @param subscriberList
 *     Array contendo a lista com todos os subscribers da person
 * @returns {object}
 */
export const SeparateSubscribers = (subscriberList) => {
  let glambox = [],
      superbox = [];

  if (Array.isArray(subscriberList)) {
    for (let n = 0; n < subscriberList.length; n++) {
      let { subscriptionId } = subscriberList[n];

      if (subscriptionId === 1) {
        glambox.push(subscriberList[n]);
      } else {
        superbox.push(subscriberList[n]);
      }
    }
  }

  return { glambox, superbox };
};

/**
 * Retorna uma lista com IDs ou objetos de subscribers.
 *
 * Condições:
 * - Caso o usuário tenha uma Glambox ativa, retorna uma lista com IDs das
 *   Glamboxes ativas + Superboxes;
 * - Caso o usuário não tenha uma Glambox ativa, retorna APENAS a Glambox mais
 *   recente + Superboxes;
 *
 * @param {object[]} subscribers
 *     Array contendo a lista de subscribers
 * @param {boolean} idOnly
 *     Se retornamos apenas o ID, ou os objetos completos
 * @returns {array}
 */
export const PossibleSubscriberIds = (subscribers, idOnly = false, isGlampartner = false) => {
  if (!subscribers || (Array.isArray(subscribers) && subscribers.length === 0)) {
    return [];
  }


  let subscriberIdList = [];
  if (HasActiveSubscriber(subscribers)) {
    let maxCancelledSubs = 0;
    for (let n = 0; n < subscribers.length; n++) {
      let curr = subscribers[n];

      if (
        curr.subscriptionId === 2
        || (curr.subscriberStatus === 0 && curr.subscriptionId === 1)
      ) {
        subscriberIdList.push(idOnly === true ? curr.id : curr);
      }
      else if(isGlampartner && maxCancelledSubs < 1){
        subscriberIdList.push(idOnly === true ? curr.id : curr);
        maxCancelledSubs++;
      }
    }

  } else {
    let separated = SeparateSubscribers(subscribers);

    // Antes: usuário pode ter apenas o `boxId` mais recente da Glambox
    // Hoje: usuário apenas tem o `boxId` da assinatura que terminou recente
    if (separated.glambox && separated.glambox.length > 0) {
      separated.glambox.sort((a, b) => {
        if (a.lastEdition !== b.lastEdition) {
          return b.lastEdition - a.lastEdition;
        }

        return b.subscriberId - a.subscriberId;
      });

      // Caso tenha assinatura New. Pega a inativa mas recente
      if(separated.glambox[0].subscriberStatus === 5 
        && separated.glambox.length > 1){
        subscriberIdList.push(
          idOnly === true
            ? separated.glambox[1].id
            : separated.glambox[1]
        );
      } else {
        subscriberIdList.push(
        idOnly === true
          ? separated.glambox[0].id
          : separated.glambox[0]
        );
      }
    }

    if (separated.superbox && separated.superbox.length > 0) {
      for (let n = 0; n < separated.superbox.length; n++) {
        subscriberIdList.push(
          idOnly === true
            ? separated.superbox[n].id
            : separated.superbox[n]
        );
      }
    }
  }

  return subscriberIdList;
};

/**
 * Retorna o nome do status de pagamento para o `billingStatusId` fornecido.
 *
 * Por padrão, retorna `null`.
 *
 * @param {number|string} billingStatusId
 *     ID do status de cobrança
 * @returns {string|null}
 */
export const BillingStatus = (billingStatusId) => {
  switch (billingStatusId) {
    case -1:  return "Teste";
    case 0:   return "Pendente";
    case 1:   return "Pago";
    case 2:   return "Falha";
    case 3:   return "Expirado";
    case 4:   return "Cancelado";
    case 5:   return "Estorno";
    case 6:   return "Parcialmente Estornado";
    default:  return null;
  }
};

/**
 * Retorna o slug para o `billingStatusId` fornecido, pode ser usado como classe
 * CSS também (isso é legado, é bom dar alguma utilidade, né?).
 *
 * @param {number|string} billingStatusId
 *     ID do status de cobrança
 * @returns {string}
 */
export const BillingStatusClass = (billingStatusId) => {
  switch (billingStatusId) {
    case -1:  return "default";
    case 0:   return "info";
    case 1:   return "success";
    case 2:   return "warning";
    case 3:   return "important";
    case 4:   return "warning";
    case 5:   return "info";
    case 6:   return "info";
    default:  return "";
  }
};

/**
 * Retorna o nome do status do assinante, para o `subscriberStatusId` fornecido.
 *
 * @param {string|number} subscriberStatusId
 *     ID do status do assinante
 * @returns {string}
 */
export const SubscriberStatus = (subscriberStatusId) => {
  switch (subscriberStatusId) {
    case 0:   return "Ativo";
    case 1:   return "Dunning";
    case 2:   return "Cancelado";
    case 3:   return "Falha no pagamento";
    case 4:   return "Expirado";
    case 5:   return "Novo";
    case 6:   return "Confirmado";
    case 7:   return "Cancelado por falha no pagamento nesse mês";
    case 8:   return "Cancelado por falha no pagamento";
    case 9:   return "Expirando";
    case 10:  return "Aguardando confirmação de pagamento";
    default:  return "Desconhecido";
  }
};

/**
 * Retorna um slug relativo ao status do assinante para o `subscriberStatusId`
 * fornecido. Não é um slug exatamente relacionado ao nome do status, mas algo
 * legal.
 *
 * @param {string|number} subscriberStatusId
 *     ID do status do assinante
 * @returns {string}
 */
export const SubscriberStatusClass = (subscriberStatusId) => {
  switch (subscriberStatusId) {
    case 0:   return "success";
    case 1:   return "attention";
    case 2:   return "problem";
    case 3:   return "attention";
    case 4:   return "problem";
    case 5:   return "success";
    case 6:   return "success";
    case 7:   return "problem";
    case 8:   return "problem";
    case 9:   return "attention";
    case 10:  return "attention";
    default:  return "";
  }
};

/**
 * Verifica se o `personStatus` é de um assinante.
 *
 * @param {number} personStatus
 *     ID do status
 * @returns {boolean}
 */
export const IsSubscriber = (personStatus) => {
  return (personStatus === 4 || personStatus === 10 || personStatus === 14);
};

/**
 * Verifica se o `personStatus` é de um ex-assinante.
 *
 * @param {number} personStatus
 *     ID do status
 * @returns {boolean}
 */
export const WasSubscriber = (personStatus) => {
  return (
    personStatus > 3
    && personStatus !== 4
    && personStatus !== 6
    && personStatus !== 7
    && personStatus !== 10
    && personStatus !== 14
  );
};

/**
 * Verifica se o `personStatus` é de um assinante ou ex-assinante.
 *
 * @param {number} personStatus
 *     ID do status
 * @returns {boolean}
 */
export const IsOrWasSubscriber = (personStatus) => {
  return (personStatus === 4 || personStatus === 5 || personStatus >= 8);
};


/**
 * Verifica se Portal Glampartners deve vir ativo
 * (quando vem de glampartners.com.br ou quando acessa /glampartners)
 *  *     
 * @returns {boolean}
 */
export const showPortalGp = () =>{
  let session = new GlamboxSessionStore();
  let showPortalGp = session.get("isGlampartner");
  return showPortalGp;
}
